import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import zIndexHandler from '../../enhancers/zindex'

// Icons
import IconMaterial from '../../common/icons/icon-material'
import IconCreateImage from '../../common/icons/icon-create-image'
import IconSaveDisk from '../../common/icons/icon-save-disk'
import { MdCenterFocusStrong as IconImageTemplate, MdTexture as IconCarrier } from 'react-icons/md'

import PanelContainer from '../../common/panels/panel-container'
import PanelButton from '../../common/panels/panel-button'

// Panels
import MaterialAndColorPanel from './material-and-color/material-and-color-panel'
import Carriers from './carriers/CarrierPanel'
import ImageTemplatesPanel from './image-templates'

import CreateRender from './buttons/create-render'
import CreateImagePackage from './buttons/create-image-package'

import connectSave from './buttons/connect-save'

import { getAnnotationsActive, getIsFeatureActive } from '../../../stores/ducks/threeviewer/selectors'
import { getSavedSceneId, getPendingExternalRename, getShowExternalRenameNotification, getScenesList, getPendingExternalDelete, getPendingExternalUpdate } from '../../../stores/ducks/scenes/selectors'
import { getIsDirty } from '../../../stores/ducks/dirty/selectors'
import * as fromScenes from '../../../stores/ducks/scenes'
import * as fromProjects from '../../../stores/ducks/projects'
import type { AppDispatch, RootState } from '../../../stores/ducks'

import { PROJECT_TYPES, FEATURES } from '../../../constants'
import SaveDialog from './saved/save-dialog'
import { ModalButton } from '../../common/modal'
import SaveErrorModal from '../../common/save-error-modal'
import UpdateNameModal from '../../common/name-update-modal'

interface ConnectSaveProps {
  disabled: boolean
  enableKeyBoardBindings: () => void
  disableKeyBoardBindings: () => void
  showSafeFrame: () => void
  resetSafeFrameVisibility: () => void
}

interface RightPanelProps {
  isTop: boolean
  setAsTop: () => void
  defaultIndex: number
  projectType: string
  isMaster: boolean
}

type TabParams = { projectType: string; annotationsActive?: boolean }

const CreateRenderAny = CreateRender as unknown as React.ComponentType<{ onClose: () => void }>

const CreateImageButton = (connectSave('render') as any)((props: ConnectSaveProps & { projectType: string; isMaster: boolean }) => (
  <ModalButton
    small
    modalContent={(closeModal: (params?: any) => void) => {
      if (props.projectType === PROJECT_TYPES.IMAGE_PACKAGE) {
        return <CreateImagePackage onClose={(params: any) => {
          props.enableKeyBoardBindings()
          closeModal(params)
        }} />
      }
      return <CreateRenderAny
        onClose={() => {
          props.resetSafeFrameVisibility()
          props.enableKeyBoardBindings()
          closeModal()
        }} />
    }}
    button={(openModal: () => void) => (
      <PanelButton
        inverted
        onClick={() => {
          props.showSafeFrame()
          props.disableKeyBoardBindings()
          openModal()
        }}
        disabled={props.disabled}
        icon={((<IconCreateImage width={28} height={28} />) as any)}
        title={(props.projectType === PROJECT_TYPES.IMAGE_PACKAGE && props.isMaster) ? 'Create Images' : 'Create Image'}
      />
    )}
    onModalClose={() => {
      props.enableKeyBoardBindings()
      props.resetSafeFrameVisibility()
    }}
  />
))

const SaveSceneButton = (connectSave('render') as any)((props: ConnectSaveProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const savedSceneId = useSelector(getSavedSceneId)
  const isDirty = useSelector(getIsDirty)
  const pendingExternalUpdate = useSelector(getPendingExternalUpdate)
  const pendingExternalRename = useSelector(getPendingExternalRename)
  const pendingExternalDelete = useSelector(getPendingExternalDelete)
  const showExternalRenameNotification = useSelector(getShowExternalRenameNotification)
  const scenesList = useSelector(getScenesList)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [placeholderTitle, setPlaceholderTitle] = useState('')

  const doSave = async () => {
    setSaveMessage('')
    dispatch(fromScenes.applyPendingExternalChanges())
    try {
      await dispatch(fromScenes.saveOrUpdateScene(savedSceneId))
    } catch {
      setIsErrorModalOpen(true)
    }
  }

  const handleClick = (openModal: () => void) => async () => {
    if (!savedSceneId) {
      props.disableKeyBoardBindings()
      openModal()
      return
    }

    if (!isDirty) {
      return
    }

    const currentScene = scenesList.find((scene) => scene.id === savedSceneId)

    if (pendingExternalDelete) {
      setSaveMessage('This save was deleted by another user. To keep your changes, save as a new copy')
      setPlaceholderTitle(`${currentScene?.title} copy`)
      dispatch(fromScenes.resetCurrentSavedSceneToUnsaved())
      props.disableKeyBoardBindings()
      openModal()
      return
    }

    if (pendingExternalRename) {
      if (currentScene?.title !== pendingExternalRename.title) {
        dispatch(fromScenes.setShowExternalRenameNotification(true))
        return
      }
    }

    if (pendingExternalUpdate) {
      if (currentScene?.updatedAt !== pendingExternalUpdate.updatedAt) {
        setSaveMessage('This save was updated by another user. To keep your changes, save as a new copy')
        setPlaceholderTitle(`${currentScene?.title} copy`)
        props.disableKeyBoardBindings()
        openModal()
        return
      }
    }

    await doSave()
  }

  const handleConfirmRenameAndSave = async () => {
    dispatch(fromScenes.setShowExternalRenameNotification(false))
    await doSave()
  }

  const handleSaveDialogSave = (closeModal: (params?: any) => void) => () => {
    dispatch(fromProjects.setNotificationMessage('Changes saved'))
    setSaveMessage('')
    setPlaceholderTitle('')
    closeModal()
  }

  return (
    <>
      <ModalButton
        small
        modalContent={(closeModal: (params?: any) => void) => (
          <SaveDialog
            onSave={handleSaveDialogSave(closeModal)}
            onClose={closeModal}
            message={saveMessage}
            placeholderTitle={placeholderTitle}
          />
        )}
        button={(openModal: () => void) => (
          <PanelButton
            inverted
            disabled={false}
            onClick={handleClick(openModal)}
            icon={((<IconSaveDisk width={28} height={28} />) as any)}
            title='Save'
          />
        )}
        onModalClose={() => {
          props.enableKeyBoardBindings()
        }}
      />
      <UpdateNameModal
        isOpen={showExternalRenameNotification}
        newName={pendingExternalRename?.title}
        onConfirm={handleConfirmRenameAndSave}
      />
      <SaveErrorModal
        isOpen={isErrorModalOpen}
        handleClose={() => setIsErrorModalOpen(false)}
      />
    </>
  )
})

const RightPanel = ({ isTop, setAsTop, defaultIndex, projectType, isMaster }: RightPanelProps) => {
  const isFeatureActive = (feature: string) => useSelector((state: RootState) => getIsFeatureActive(state)(feature, true))
  const isMasterScene = (isMaster && projectType === PROJECT_TYPES.IMAGE_PACKAGE)
  const annotationsActive = useSelector((state: RootState) => getAnnotationsActive(state))
  const isColorStrengthOpen = useSelector((state: RootState) => state.colors.colorStrengthOpen)

  const tabs = [
    {
      icon: (<IconMaterial width={26} height={26} /> as React.ReactElement),
      title: 'Appearance',
      content: <MaterialAndColorPanel height={500} />,
      shouldDisplay: ({ projectType }: TabParams) => projectType === PROJECT_TYPES.DEFAULT || isMasterScene,
      isDisabled: annotationsActive
    },
    {
      icon: (<IconImageTemplate size={26} /> as React.ReactElement),
      title: 'Image Templates',
      keepMounted: true,
      content: <ImageTemplatesPanel active={false} />,
      defaultWidth: 250,
      shouldDisplay: ({ projectType }: TabParams) => isFeatureActive(FEATURES.IMAGE_TEMPLATES) && projectType === PROJECT_TYPES.DEFAULT,
      isDisabled: annotationsActive
    },
    {
      icon: (<IconCarrier size='26' /> as React.ReactElement),
      title: 'Materials',
      content: <Carriers />,
      shouldDisplay: ({ projectType }: TabParams) => isFeatureActive(FEATURES.PRODUCT_CONCEPT_ANALYSIS) && (projectType === PROJECT_TYPES.DEFAULT || isMasterScene),
      isDisabled: annotationsActive
    }
  ]

  const filteredTabs = tabs
    .filter(tab => tab.shouldDisplay({ projectType, annotationsActive }))
    .map(tab => ({ ...tab, disabled: tab.isDisabled }))

  return (
    <PanelContainer {...({} as any)}
      className={isTop ? 'z3' : 'z2'}
      defaultIndex={defaultIndex}
      defaultWidth={projectType === PROJECT_TYPES.IMAGE_PACKAGE ? 380 : 400}
      minWidth={isColorStrengthOpen ? 380 : 300}
      maxWidth={500}
      onClick={setAsTop}
      position='right'
      tabs={filteredTabs as any}
    >
      <SaveSceneButton />
      <CreateImageButton projectType={projectType} isMaster={isMaster} />

    </PanelContainer>
  )
}

export default zIndexHandler(RightPanel)
